import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Segmented, Space, Switch, Select, Button, Tag, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, StarOutlined } from '@ant-design/icons';
import {
  AppSettings,
  PeachPersonalItem,
  PeachLeagueItem,
  PeachGlobalItem,
  PeachSnapshot,
} from '../types';
import { peachAPI } from '../services/api';

interface Props {
  settings: AppSettings;
}

type BoardMode = 'personal' | 'league' | 'global';

type BoardKey = string;

interface BoardOption {
  key: BoardKey;
  label: string;
  mode: BoardMode;
  categoryCode?: string;
}

type PersonalOrder = 'score' | 'fightpoint' | 'lv';
type LeagueOrder = 'score' | 'member_count';
type GlobalOrder = 'score' | 'fightpoint' | 'lv';

type OrderState = {
  personal: PersonalOrder;
  league: LeagueOrder;
  global: GlobalOrder;
};

const ACTIVITY_OPTIONS = [
  { key: 'peach', label: '蟠桃盛宴（蟠桃榜）' },
];

const BASE_BOARD_SEGMENTS: ReadonlyArray<BoardOption> = [
  { key: 'personal', label: '积分榜', mode: 'personal' },
  // { key: 'league', label: '联盟榜', mode: 'league' }, // 暂时隐藏
];

const GLOBAL_BOARD_SEGMENTS: ReadonlyArray<BoardOption> = [
  { key: 'global-20633', label: '金龙天尊', mode: 'global', categoryCode: '20633' },
  { key: 'global-90103', label: '红玉仙官', mode: 'global', categoryCode: '90103' },
  { key: 'global-90104', label: '碧源仙子', mode: 'global', categoryCode: '90104' },
];

const SEGMENT_OPTIONS: ReadonlyArray<BoardOption> = [
  ...BASE_BOARD_SEGMENTS,
  ...GLOBAL_BOARD_SEGMENTS,
];

const injectFilteredRank = <T extends Record<string, any>>(items: T[], page: number, pageSize: number) =>
  items.map((item, index) => ({ ...item, filtered_rank: (page - 1) * pageSize + index + 1 }));

const { Text } = Typography;

const ORDER_OPTIONS: Record<BoardMode, { value: string; label: string }[]> = {
  personal: [
    { value: 'score', label: '按积分' },
    { value: 'fightpoint', label: '按战力' },
    { value: 'lv', label: '按等级' },
  ],
  league: [
    { value: 'score', label: '按积分' },
    { value: 'member_count', label: '按成员数' },
  ],
  global: [
    { value: 'score', label: '按积分' },
    { value: 'fightpoint', label: '按战力' },
    { value: 'lv', label: '按等级' },
  ],
};

// const formatDateTime = (value?: string | null) => {
//   if (!value) return '';
//   const d = new Date(value);
//   if (Number.isNaN(d.getTime())) {
//     return value;
//   }
//   return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
// };

const formatScoreDisplaySimple = (formatted?: string | null, raw?: number | null) => {
  if (formatted) {
    return formatted.replace(/,/g, '');
  }
  if (raw != null) {
    return raw.toString();
  }
  return '-';
};

const getSnapshotDisplay = (snapshot: PeachSnapshot | undefined) => {
  if (!snapshot) {
    return '';
  }
  // const timeText = formatDateTime(snapshot.collected_at || snapshot.created_at);
  // return timeText || snapshot.event_phase || `快照 ${snapshot.id}`;
  return snapshot.event_phase || `快照 ${snapshot.id}`;
};

const getSnapshotTimestamp = (snapshot?: PeachSnapshot | null) => {
  if (!snapshot) {
    return 0;
  }
  const value = snapshot.collected_at || snapshot.created_at || snapshot.event_phase;
  if (!value) {
    return 0;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const sortSnapshotsDesc = (a: PeachSnapshot, b: PeachSnapshot) => getSnapshotTimestamp(b) - getSnapshotTimestamp(a);

const ActivityRankings: React.FC<Props> = ({ settings }) => {
  const [selectedActivity, setSelectedActivity] = useState('peach');
  const [boardKey, setBoardKey] = useState<BoardKey>('personal');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [useHistory] = useState(true); // 默认开启历史模式
  const [snapshotId, setSnapshotId] = useState<number | undefined>(undefined);
  const [snapshots, setSnapshots] = useState<PeachSnapshot[]>([]);
  const [activeSnapshot, setActiveSnapshot] = useState<PeachSnapshot | undefined>(undefined);
  const [comparisonSnapshot, setComparisonSnapshot] = useState<PeachSnapshot | undefined>(undefined);
  const [compareEnabled, setCompareEnabled] = useState(() => {
    // 从localStorage读取对比开关状态
    const saved = localStorage.getItem('activityRankings_compareEnabled');
    return saved === 'true';
  });
  const [baseSnapshotId, setBaseSnapshotId] = useState<number | undefined>(undefined);
  const [orderState, setOrderState] = useState<OrderState>({ personal: 'score', league: 'score', global: 'score' });
  const segmentOptions = SEGMENT_OPTIONS;
  const globalCategoryOverridesRef = useRef<Record<string, string>>({});
  const sortedSnapshots = useMemo(() => {
    return snapshots.slice().sort(sortSnapshotsDesc);
  }, [snapshots]);

  const compareActive = useMemo(() => (
    compareEnabled && useHistory && snapshotId != null && baseSnapshotId != null && snapshotId !== baseSnapshotId
  ), [compareEnabled, useHistory, snapshotId, baseSnapshotId]);

  useEffect(() => {
    if (!segmentOptions.some(option => option.key === boardKey)) {
      setBoardKey(segmentOptions[0].key);
    }
  }, [boardKey, segmentOptions]);

  const selectedBoard = useMemo<BoardOption>(() => {
    return segmentOptions.find(item => item.key === boardKey) || segmentOptions[0];
  }, [segmentOptions, boardKey]);

  const currentOrderBy = useMemo(() => orderState[selectedBoard.mode], [orderState, selectedBoard.mode]);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const resp = await peachAPI.listSnapshots({ limit: 60 });
        setSnapshots(resp.snapshots || []);
      } catch (error) {
        console.error(error);
        message.error('获取桃宴活动快照失败');
      }
    };
    fetchSnapshots();
  }, []);

  useEffect(() => {
    setPage(1);
    setData([]);
    setTotal(0);
    setActiveSnapshot(undefined);
    setComparisonSnapshot(undefined);
    // 保持对比状态，不重置 compareEnabled 和 baseSnapshotId
  }, [selectedBoard]);

  useEffect(() => {
    if (sortedSnapshots.length === 0) {
      if (snapshotId !== undefined) {
        setSnapshotId(undefined);
      }
      if (baseSnapshotId !== undefined) {
        setBaseSnapshotId(undefined);
      }
      return;
    }

    const hasTarget = snapshotId != null && sortedSnapshots.some(snap => snap.id === snapshotId);
    if (!hasTarget) {
      setSnapshotId(sortedSnapshots[0].id);
    }
  }, [snapshotId, sortedSnapshots, baseSnapshotId]);

  useEffect(() => {
    if (!compareEnabled) {
      setComparisonSnapshot(undefined);
      return;
    }
    if (sortedSnapshots.length < 2) {
      setCompareEnabled(false);
      setBaseSnapshotId(undefined);
      message.warning('可用快照不足，无法开启对比模式');
      return;
    }

    const currentTargetId = snapshotId ?? sortedSnapshots[0]?.id;
    if (currentTargetId == null) {
      return;
    }

    if (baseSnapshotId == null || baseSnapshotId === currentTargetId || !sortedSnapshots.some(snap => snap.id === baseSnapshotId)) {
      const fallback = sortedSnapshots.find(snap => snap.id !== currentTargetId);
      setBaseSnapshotId(fallback?.id);
    }
  }, [compareEnabled, snapshotId, sortedSnapshots, baseSnapshotId]);

  // 历史模式默认开启，不需要相关useEffect

  const makePersonalKey = (item: any) => {
    if (!item) return 'unknown';
    if (item.player_uid) return `uid:${item.player_uid}`;
    const name = item.player_name ?? 'unknown';
    const server = item.server_id ?? 'all';
    return `name:${name}|server:${server}`;
  };

  const makeGlobalKey = (item: any) => {
    if (!item) return 'unknown';
    if (item.player_uid) return `uid:${item.player_uid}`;
    const name = item.player_name ?? 'unknown';
    const server = item.server_id ?? 'all';
    return `name:${name}|server:${server}`;
  };

  const makeLeagueKey = (item: any) => {
    if (!item) return 'unknown';
    if (item.league_id) return `league:${item.league_id}`;
    const name = item.league_name ?? 'unknown';
    const server = item.server_id ?? 'all';
    return `name:${name}|server:${server}`;
  };

  const calcDelta = (current?: number | null, previous?: number | null) => (current ?? 0) - (previous ?? 0);

  const getBaselineRank = useCallback((item: any, mode: BoardMode) => {
    if (!item) {
      return undefined;
    }
    if (mode === 'league') {
      return item.rank ?? undefined;
    }
    if (mode === 'personal') {
      if (settings.serverId != null) {
        return item.server_rank ?? item.rank ?? item.global_rank ?? undefined;
      }
      return item.global_rank ?? item.rank ?? item.server_rank ?? undefined;
    }
    // global
    return item.global_rank ?? item.rank ?? undefined;
  }, [settings.serverId]);

  const loadRankings = useCallback(async (targetPage = 1) => {
    if (selectedActivity !== 'peach') {
      setData([]);
      setTotal(0);
      setActiveSnapshot(undefined);
      setComparisonSnapshot(undefined);
      return;
    }

    setLoading(true);
    try {
      const pageSize = settings.pageSize;
      const targetSnapshotId = snapshotId ?? null;
      const baseSnapshotParam = compareActive ? baseSnapshotId ?? null : null;

      const fetchPersonal = async (
        snapshotValue: number | null | undefined,
        pageArg: number,
        pageSizeArg: number,
      ) => peachAPI.getPersonal({
        page: pageArg,
        page_size: pageSizeArg,
        server_id: settings.serverId ?? undefined,
        snapshot_id: snapshotValue ?? null,
        order_by: orderState.personal,
        use_units: settings.useUnits,
      });

      const fetchLeague = async (
        snapshotValue: number | null | undefined,
        pageArg: number,
        pageSizeArg: number,
      ) => peachAPI.getLeague({
        page: pageArg,
        page_size: pageSizeArg,
        server_id: settings.serverId ?? undefined,
        snapshot_id: snapshotValue ?? null,
        order_by: orderState.league,
        use_units: settings.useUnits,
      });

      const fetchGlobalRaw = async (
        snapshotValue: number | null | undefined,
        categoryCode: string | null | undefined,
        pageArg: number,
        pageSizeArg: number,
      ) => peachAPI.getGlobal({
        page: pageArg,
        page_size: pageSizeArg,
        category_code: categoryCode ?? null,
        snapshot_id: snapshotValue ?? null,
        order_by: orderState.global,
        use_units: settings.useUnits,
      });

      if (selectedBoard.mode === 'personal') {
        if (compareActive && baseSnapshotParam != null) {
          const [targetResp, baseResp] = await Promise.all([
            fetchPersonal(targetSnapshotId, targetPage, pageSize),
            fetchPersonal(baseSnapshotParam, targetPage, pageSize),
          ]);
          const baseMap = new Map((baseResp.items ?? []).map((item: any) => [makePersonalKey(item), item]));
          const processed = (targetResp.items ?? []).map((item: any, index: number) => {
            const key = makePersonalKey(item);
            const baseItem = baseMap.get(key);
            const currentRank = getBaselineRank(item, 'personal');
            const previousRank = getBaselineRank(baseItem, 'personal');
            return {
              ...item,
              filtered_rank: (targetPage - 1) * pageSize + index + 1,
              score_delta: calcDelta(item.score, baseItem?.score),
              rank_delta: previousRank != null && currentRank != null ? currentRank - previousRank : null,
              previous_rank: previousRank ?? null,
              is_new: !baseItem,
            };
          });
          setData(processed);
          setTotal(targetResp.total ?? processed.length);
          setPage(targetResp.page ?? targetPage);
          setActiveSnapshot(targetResp.snapshot ?? undefined);
          setComparisonSnapshot(baseResp.snapshot ?? undefined);
        } else {
          const response = await fetchPersonal(targetSnapshotId, targetPage, pageSize);
          const items = injectFilteredRank(response.items ?? [], targetPage, pageSize);
          setData(items);
          setTotal(response.total ?? items.length);
          setPage(response.page ?? targetPage);
          setActiveSnapshot(response.snapshot ?? undefined);
          setComparisonSnapshot(undefined);
        }
      } else if (selectedBoard.mode === 'league') {
        if (compareActive && baseSnapshotParam != null) {
          const [targetResp, baseResp] = await Promise.all([
            fetchLeague(targetSnapshotId, targetPage, pageSize),
            fetchLeague(baseSnapshotParam, targetPage, pageSize),
          ]);
          const baseMap = new Map((baseResp.items ?? []).map((item: any) => [makeLeagueKey(item), item]));
          const processed = (targetResp.items ?? []).map((item: any, index: number) => {
            const key = makeLeagueKey(item);
            const baseItem = baseMap.get(key);
            const currentRank = getBaselineRank(item, 'league');
            const previousRank = getBaselineRank(baseItem, 'league');
            const record: any = {
              ...item,
              filtered_rank: (targetPage - 1) * pageSize + index + 1,
              score_delta: calcDelta(item.score, baseItem?.score),
              member_delta: calcDelta(item.member_count, baseItem?.member_count),
              rank_delta: previousRank != null && currentRank != null ? currentRank - previousRank : null,
              previous_rank: previousRank ?? null,
              is_new: !baseItem,
            };
            if (!record.league_name) {
              const fallbackName = record.leader_name || (record.server_id != null ? `服务器${record.server_id}联盟` : undefined);
              if (fallbackName) {
                record.league_name = fallbackName;
              }
            }
            return record;
          });
          setData(processed);
          setTotal(targetResp.total ?? processed.length);
          setPage(targetResp.page ?? targetPage);
          setActiveSnapshot(targetResp.snapshot ?? undefined);
          setComparisonSnapshot(baseResp.snapshot ?? undefined);
        } else {
          const response = await fetchLeague(targetSnapshotId, targetPage, pageSize);
          const items = injectFilteredRank(response.items ?? [], targetPage, pageSize).map(item => {
            if (!item.league_name) {
              const fallbackName = item.leader_name || (item.server_id != null ? `服务器${item.server_id}联盟` : undefined);
              if (fallbackName) {
                return { ...item, league_name: fallbackName };
              }
            }
            return item;
          });
          setData(items);
          setTotal(response.total ?? items.length);
          setPage(response.page ?? targetPage);
          setActiveSnapshot(response.snapshot ?? undefined);
          setComparisonSnapshot(undefined);
        }
      } else {
        const overrideMap = globalCategoryOverridesRef.current;
        let appliedCategoryCode = overrideMap[selectedBoard.key] ?? selectedBoard.categoryCode ?? null;

        const fetchGlobalWithFallback = async (
          snapshotValue: number | null | undefined,
          categoryCode: string | null | undefined,
          updateOverrides: boolean,
        ) => {
          let code = categoryCode;
          let response = await fetchGlobalRaw(snapshotValue, code, targetPage, pageSize);

          if ((!response.items || response.items.length === 0) && response.categories?.length) {
            const baseCode = selectedBoard.categoryCode ?? undefined;
            const matchedCode = response.categories.find(cat => {
              if (!baseCode) {
                return !!cat;
              }
              return cat === baseCode || cat.startsWith(baseCode) || baseCode.startsWith(cat);
            }) ?? response.categories[0];

            if (matchedCode && matchedCode !== code) {
              if (updateOverrides) {
                globalCategoryOverridesRef.current = {
                  ...globalCategoryOverridesRef.current,
                  [selectedBoard.key]: matchedCode,
                };
              }
              code = matchedCode;
              response = await fetchGlobalRaw(snapshotValue, code, targetPage, pageSize);
            }
          }

          if (response.categories?.length && updateOverrides) {
            const overrideCopy = { ...globalCategoryOverridesRef.current };
            GLOBAL_BOARD_SEGMENTS.forEach(segment => {
              const baseCode = segment.categoryCode;
              if (!baseCode) {
                return;
              }
              const detected = response.categories?.find(cat =>
                cat === baseCode ||
                cat.startsWith(baseCode) ||
                baseCode.startsWith(cat),
              );
              if (detected) {
                overrideCopy[segment.key] = detected;
              }
            });
            globalCategoryOverridesRef.current = overrideCopy;
          }

          return { response, code };
        };

        const { response: targetResp, code: resolvedCode } = await fetchGlobalWithFallback(
          targetSnapshotId,
          appliedCategoryCode,
          true,
        );

        if (compareActive && baseSnapshotParam != null) {
          const baseResult = await fetchGlobalWithFallback(baseSnapshotParam, resolvedCode, false);
          const baseMap = new Map((baseResult.response.items ?? []).map((item: any) => [makeGlobalKey(item), item]));
          const processed = (targetResp.items ?? []).map((item: any, index: number) => {
            const key = makeGlobalKey(item);
            const baseItem = baseMap.get(key);
            const currentRank = getBaselineRank(item, 'global');
            const previousRank = getBaselineRank(baseItem, 'global');
            return {
              ...item,
              filtered_rank: (targetPage - 1) * pageSize + index + 1,
              score_delta: calcDelta(item.score, baseItem?.score),
              rank_delta: previousRank != null && currentRank != null ? currentRank - previousRank : null,
              previous_rank: previousRank ?? null,
              is_new: !baseItem,
            };
          });
          setData(processed);
          setTotal(targetResp.total ?? processed.length);
          setPage(targetResp.page ?? targetPage);
          setActiveSnapshot(targetResp.snapshot ?? undefined);
          setComparisonSnapshot(baseResult.response.snapshot ?? undefined);
        } else {
          const items = injectFilteredRank(targetResp.items ?? [], targetPage, pageSize);
          setData(items);
          setTotal(targetResp.total ?? items.length);
          setPage(targetResp.page ?? targetPage);
          setActiveSnapshot(targetResp.snapshot ?? undefined);
          setComparisonSnapshot(undefined);
        }
      }
    } catch (error) {
      console.error(error);
      message.error('加载活动排行榜失败');
    } finally {
      setLoading(false);
    }
  }, [
    selectedActivity,
    selectedBoard,
    settings.pageSize,
    settings.useUnits,
    settings.serverId,
    snapshotId,
    orderState,
    compareActive,
    baseSnapshotId,
    getBaselineRank,
  ]);

  useEffect(() => {
    loadRankings(1);
  }, [loadRankings]);

  const columns = useMemo<ColumnsType<any>>(() => {
    if (selectedBoard.mode === 'personal') {
      const cols: ColumnsType<PeachPersonalItem> = [
        {
          title: '全服排名',
          dataIndex: 'global_rank',
          key: 'global_rank',
          width: 100,
          align: 'center',
          render: (rank: number | null | undefined) => (
            <Tag color={rank && rank <= 3 ? 'gold' : rank && rank <= 10 ? 'orange' : 'default'}>{rank ?? '-'}</Tag>
          ),
        },
        {
          title: '本服排名',
          dataIndex: 'server_rank',
          key: 'server_rank',
          width: 100,
          align: 'center',
          render: (value: number | null | undefined) => value ?? '-',
        },
        {
          title: '玩家',
          dataIndex: 'player_name',
          key: 'player_name',
          width: 160,
          render: (name: string | null | undefined) => <Text strong style={{ color: '#1890ff' }}>{name || '未知玩家'}</Text>,
        },
        {
          title: 'UID',
          dataIndex: 'player_uid',
          key: 'player_uid',
          width: 180,
          render: (uid: string | null | undefined) => <Text type="secondary">{uid || '-'}</Text>,
        },
        {
          title: '积分',
          dataIndex: 'formatted_score',
          key: 'score',
          width: 120,
          align: 'center',
          render: (_: any, record) => (
            <span style={{ color: '#fa8c16', fontWeight: 600 }}>{formatScoreDisplaySimple(record.formatted_score, record.score)}</span>
          ),
        },
        {
          title: '等级',
          dataIndex: 'lv',
          key: 'lv',
          width: 90,
          align: 'center',
          render: (lv: number | null | undefined) => (
            <span style={{ fontWeight: 'bold', color: '#52c41a' }}>{lv ?? '-'}</span>
          ),
        },
        {
          title: '战力',
          dataIndex: 'formatted_fightpoint',
          key: 'fightpoint',
          width: 140,
          align: 'center',
          render: (_: any, record) => (
            <span style={{ fontWeight: 'bold', color: '#f5222d' }}>
              {formatScoreDisplaySimple(record.formatted_fightpoint, record.fightpoint)}
            </span>
          ),
        },
        {
          title: '联盟',
          dataIndex: 'league_name',
          key: 'league_name',
          width: 160,
          render: (val: string | null | undefined) => (
            <span style={{ fontWeight: 'bold', color: '#722ed1' }}>{val || '-'}</span>
          ),
        },
        {
          title: '地区',
          dataIndex: 'ip_area',
          key: 'ip_area',
          width: 120,
          render: (val: string | null | undefined) => val || '-',
        },
        {
          title: '服务器',
          dataIndex: 'server_id',
          key: 'server_id',
          width: 100,
          align: 'center',
          render: (value: number) => value ?? '-',
        },
        {
          title: '更新时间',
          dataIndex: 'time_iso',
          key: 'time_iso',
          width: 170,
          // render: (value: string | null | undefined) => formatDateTime(value) || '-',
        },
      ];
      if (compareActive) {
        cols.push(
          {
            title: '积分变化',
            dataIndex: 'score_delta',
            key: 'score_delta',
            width: 120,
            align: 'center',
            render: (delta: number | null | undefined) => renderNumericDelta(delta),
          },
          {
            title: '排名变化',
            dataIndex: 'rank_delta',
            key: 'rank_delta',
            width: 120,
            align: 'center',
            render: (delta: number | null | undefined, record: any) => renderRankDeltaCell(delta, record),
          },
        );
      }
      return cols as ColumnsType<any>;
    }

    if (selectedBoard.mode === 'league') {
      const cols: ColumnsType<PeachLeagueItem> = [
        { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center' },
        {
          title: '联盟',
          dataIndex: 'league_name',
          key: 'league_name',
          width: 200,
          render: (name: string | null | undefined) => <Text strong>{name || '未知联盟'}</Text>,
        },
        {
          title: '盟主',
          dataIndex: 'leader_name',
          key: 'leader_name',
          width: 140,
          render: (val: string | null | undefined) => val || '-',
        },
        {
          title: '成员数',
          dataIndex: 'member_count',
          key: 'member_count',
          width: 100,
          align: 'center',
          render: (val: number | null | undefined) => val ?? '-',
        },
        {
          title: '积分',
          dataIndex: 'formatted_score',
          key: 'score',
          width: 120,
          align: 'center',
          render: (_: any, record) => (
            <span style={{ color: '#f5222d', fontWeight: 600 }}>{formatScoreDisplaySimple(record.formatted_score, record.score)}</span>
          ),
        },
        {
          title: '更新时间',
          dataIndex: 'time_iso',
          key: 'time_iso',
          width: 160,
          // render: (value: string | null | undefined) => formatDateTime(value) || '-',
        },
      ];
      if (compareActive) {
        cols.push(
          {
            title: '积分变化',
            dataIndex: 'score_delta',
            key: 'score_delta',
            width: 120,
            align: 'center',
            render: (delta: number | null | undefined) => renderNumericDelta(delta),
          },
          {
            title: '成员变化',
            dataIndex: 'member_delta',
            key: 'member_delta',
            width: 120,
            align: 'center',
            render: (delta: number | null | undefined) => renderNumericDelta(delta),
          },
          {
            title: '排名变化',
            dataIndex: 'rank_delta',
            key: 'rank_delta',
            width: 120,
            align: 'center',
            render: (delta: number | null | undefined, record: any) => renderRankDeltaCell(delta, record),
          },
        );
      }
      return cols as ColumnsType<any>;
    }

    const cols: ColumnsType<PeachGlobalItem> = [
      {
        title: '全服排名',
        dataIndex: 'global_rank',
        key: 'global_rank',
        width: 100,
        align: 'center',
        render: (rank: number | null | undefined) => (
          <Tag color={rank && rank <= 3 ? 'gold' : rank && rank <= 10 ? 'orange' : 'default'}>{rank ?? '-'}</Tag>
        ),
      },
      {
        title: '玩家',
        dataIndex: 'player_name',
        key: 'player_name',
        width: 160,
        render: (name: string | null | undefined) => <Text strong style={{ color: '#1890ff' }}>{name || '未知玩家'}</Text>,
      },
      {
        title: 'UID',
        dataIndex: 'player_uid',
        key: 'player_uid',
        width: 180,
        render: (uid: string | null | undefined) => <Text type="secondary">{uid || '-'}</Text>,
      },
      {
        title: '积分',
        dataIndex: 'formatted_score',
        key: 'score',
        width: 120,
        align: 'center',
        render: (_: any, record) => (
          <span style={{ color: '#fa8c16', fontWeight: 600 }}>
            {formatScoreDisplaySimple(record.formatted_score, record.score)}
          </span>
        ),
      },
      {
        title: '战力',
        dataIndex: 'formatted_fightpoint',
        key: 'fightpoint',
        width: 140,
        align: 'center',
        render: (_: any, record) => (
          <span style={{ fontWeight: 'bold', color: '#f5222d' }}>
            {formatScoreDisplaySimple(record.formatted_fightpoint, record.fightpoint)}
          </span>
        ),
      },
      {
        title: '等级',
        dataIndex: 'lv',
        key: 'lv',
        width: 90,
        align: 'center',
        render: (lv: number | null | undefined) => (
          <span style={{ fontWeight: 'bold', color: '#52c41a' }}>{lv ?? '-'}</span>
        ),
      },
      {
        title: '联盟',
        dataIndex: 'league_name',
        key: 'league_name',
        width: 160,
        render: (val: string | null | undefined) => (
          <span style={{ fontWeight: 'bold', color: '#722ed1' }}>{val || '-'}</span>
        ),
      },
      {
        title: '地区',
        dataIndex: 'ip_area',
        key: 'ip_area',
        width: 120,
        render: (val: string | null | undefined) => val || '-',
      },
      {
        title: '所属区服',
        dataIndex: 'server_id',
        key: 'server_id',
        width: 120,
        align: 'center',
        render: (value: number | null | undefined) => value ?? '-',
      },
      {
        title: '更新时间',
        dataIndex: 'time_iso',
        key: 'time_iso',
        width: 170,
  // render: (value: string | null | undefined) => formatDateTime(value) || '-',
      },
    ];
    if (compareActive) {
      cols.push(
        {
          title: '积分变化',
          dataIndex: 'score_delta',
          key: 'score_delta',
          width: 120,
          align: 'center',
          render: (delta: number | null | undefined) => renderNumericDelta(delta),
        },
        {
          title: '排名变化',
          dataIndex: 'rank_delta',
          key: 'rank_delta',
          width: 120,
          align: 'center',
          render: (delta: number | null | undefined, record: any) => renderRankDeltaCell(delta, record),
        },
      );
    }
    return cols as ColumnsType<any>;
  }, [selectedBoard.mode, compareActive]);

  const handleRefresh = () => {
    loadRankings(page);
  };

  const handleCompareToggle = (checked: boolean) => {
    if (checked) {
      if (!snapshotId && sortedSnapshots.length > 0) {
        setSnapshotId(sortedSnapshots[0].id);
      }
    } else {
      setComparisonSnapshot(undefined);
      setBaseSnapshotId(undefined);
    }
    setCompareEnabled(checked);
    // 保存状态到localStorage
    localStorage.setItem('activityRankings_compareEnabled', checked.toString());
    setPage(1);
  };

  const handleBaseSnapshotChange = (value: number) => {
    if (value === snapshotId) {
      message.warning('对比快照需要与目标快照不同');
      return;
    }
    setBaseSnapshotId(value);
    setPage(1);
  };

  const renderNumericDelta = (delta: number | null | undefined) => {
    if (delta == null || Number.isNaN(delta)) {
      return <span style={{ color: '#999' }}>—</span>;
    }
    if (delta === 0) {
      return <span style={{ color: '#666' }}>0</span>;
    }
    const color = delta > 0 ? '#52c41a' : '#f5222d';
    const text = delta > 0 ? `+${delta}` : `${delta}`;
    return <span style={{ color, fontWeight: 600 }}>{text}</span>;
  };

  const renderRankDeltaCell = (delta: number | null | undefined, record: any) => {
    if (record?.is_new) {
      return <Tag color="green" style={{ fontWeight: 700 }}>新上榜</Tag>;
    }
    if (delta == null || Number.isNaN(delta)) {
      return <span style={{ color: '#999' }}>—</span>;
    }
    if (delta === 0) {
      return <span style={{ color: '#666' }}>—</span>;
    }
    const improved = delta < 0;
    const color = improved ? '#52c41a' : '#f5222d';
    const text = improved ? `⬆ ${Math.abs(delta)}` : `⬇︎ ${delta}`;
    return <span style={{ color, fontWeight: 700 }}>{text}</span>;
  };

  const handleOrderChange = useCallback((value: string) => {
    let changed = false;
    setOrderState(prev => {
      if (selectedBoard.mode === 'personal') {
        if (prev.personal === value) {
          return prev;
        }
        changed = true;
        return { ...prev, personal: value as PersonalOrder };
      }
      if (selectedBoard.mode === 'league') {
        if (prev.league === value) {
          return prev;
        }
        changed = true;
        return { ...prev, league: value as LeagueOrder };
      }
      if (prev.global === value) {
        return prev;
      }
      changed = true;
      return { ...prev, global: value as GlobalOrder };
    });
    if (changed) {
      setPage(1);
    }
  }, [selectedBoard.mode]);

  const rowKey = useCallback((record: any) => {
    if (selectedBoard.mode === 'personal') {
      const rankKey = record.global_rank ?? record.server_rank ?? record.rank ?? record.filtered_rank ?? 'unknown';
      return `personal-${record.server_id ?? 'all'}-${rankKey}-${record.player_uid ?? record.player_name ?? 'unknown'}`;
    }
    if (selectedBoard.mode === 'league') {
      return `league-${record.server_id ?? 'all'}-${record.rank ?? record.filtered_rank ?? 'unknown'}-${record.league_id ?? record.league_name ?? 'unknown'}`;
    }
    const rankKey = record.global_rank ?? record.rank ?? record.filtered_rank ?? 'unknown';
    const overrideMap = globalCategoryOverridesRef.current;
    const categoryKey = record.category_code ?? overrideMap[selectedBoard.key] ?? selectedBoard.categoryCode ?? 'all';
    return `global-${categoryKey}-${rankKey}-${record.player_uid ?? record.player_name ?? 'unknown'}`;
  }, [selectedBoard]);

  return (
    <Card
      title={(
        <Space>
          <StarOutlined />
          活动排行榜
        </Space>
      )}
      extra={(
        <Space>
          <Select
            value={selectedActivity}
            options={ACTIVITY_OPTIONS.map(opt => ({ value: opt.key, label: opt.label }))}
            style={{ width: 200 }}
            onChange={(value) => setSelectedActivity(value)}
            disabled={ACTIVITY_OPTIONS.length <= 1}
          />
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            刷新
          </Button>
        </Space>
      )}
      bordered={false}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space wrap align="center">
          <Segmented
            value={boardKey}
            onChange={(val) => setBoardKey(val as BoardKey)}
            options={segmentOptions.map(opt => ({ label: opt.label, value: opt.key }))}
          />
          <Select
            value={snapshotId}
            onChange={(value: number) => { setSnapshotId(value); setPage(1); }}
            style={{ minWidth: 260 }}
            placeholder="选择目标快照"
            options={sortedSnapshots.map(snap => ({ value: snap.id, label: getSnapshotDisplay(snap) }))}
            allowClear={false}
            size="small"
          />
          <Space size={4} align="center">
            <Text type="secondary">对比</Text>
            <Switch
              checked={compareEnabled}
              onChange={handleCompareToggle}
              size="small"
              disabled={sortedSnapshots.length < 2}
            />
          </Space>
          {compareEnabled && (
            <Select
              value={baseSnapshotId}
              onChange={handleBaseSnapshotChange}
              style={{ minWidth: 260 }}
              placeholder="选择对比快照"
              options={sortedSnapshots
                .filter(snap => snapshotId == null || snap.id !== snapshotId)
                .map(snap => ({ value: snap.id, label: getSnapshotDisplay(snap) }))}
              allowClear={false}
              size="small"
              disabled={sortedSnapshots.length < 2}
            />
          )}
          {useHistory && activeSnapshot && (
            <Text type="secondary">
              目标快照：{getSnapshotDisplay(activeSnapshot)}
            </Text>
          )}
          {compareActive && comparisonSnapshot && (
            <Text type="secondary">
              对比快照：{getSnapshotDisplay(comparisonSnapshot)}
            </Text>
          )}
          <Space size={4} align="center">
            <Text type="secondary">排序</Text>
            <Select
              value={currentOrderBy}
              options={ORDER_OPTIONS[selectedBoard.mode]}
              onChange={handleOrderChange}
              style={{ width: 150 }}
              size="small"
            />
          </Space>
        </Space>

        {selectedBoard.mode === 'personal' && settings.serverId == null && (
          <Text type="secondary">
            当前未选择服务器，将展示全服汇总结果。可在系统设置中指定默认服务器以查看本区数据。
          </Text>
        )}

        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={rowKey}
          pagination={{
            current: page,
            pageSize: settings.pageSize,
            total,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => loadRankings(p),
          }}
          scroll={{ x: 1280 }}
          sticky={{ offsetHeader: 64 }}
          bordered
          size="middle"
        />
      </Space>
    </Card>
  );
};

export default ActivityRankings;
